import React, { useState } from 'react';
import FeedbackNotification from '../common/FeedbackNotification';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';

const PaymentMethodForm = ({ onSave, onCancel, existingMethod = null }) => {
  const [formData, setFormData] = useState({
    type: existingMethod?.type || 'paypal',
    details: existingMethod?.details || {},
    isDefault: existingMethod?.isDefault || false
  });

  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      details: name.startsWith('details.') 
        ? { ...prev.details, [name.replace('details.', '')]: value }
        : prev.details
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate based on payment method type
    if (formData.type === 'paypal') {
      if (!formData.details.email) {
        newErrors['details.email'] = 'PayPal email is required';
      } else if (!/^\\S+@\\S+\\.\\S+$/.test(formData.details.email)) {
        newErrors['details.email'] = 'Please enter a valid email address';
      }
    } else if (formData.type === 'bank') {
      if (!formData.details.accountName) {
        newErrors['details.accountName'] = 'Account name is required';
      }
      if (!formData.details.accountNumber) {
        newErrors['details.accountNumber'] = 'Account number is required';
      }
      if (!formData.details.bankName) {
        newErrors['details.bankName'] = 'Bank name is required';
      }
    } else if (formData.type === 'gcash') {
      if (!formData.details.phoneNumber) {
        newErrors['details.phoneNumber'] = 'GCash phone number is required';
      } else if (!/^\\d{11}$/.test(formData.details.phoneNumber)) {
        newErrors['details.phoneNumber'] = 'Please enter a valid 11-digit phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        onSave({
          id: existingMethod?.id || `method-${Date.now()}`,
          type: formData.type,
          details: formData.details,
          isDefault: formData.isDefault,
          dateAdded: existingMethod?.dateAdded || new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        });
        
        // Show success feedback
        setFeedbackType('success');
        setFeedbackMessage('Payment method saved successfully!');
        setShowFeedback(true);
        
        // Clear feedback after animation completes
        setTimeout(() => {
          setShowFeedback(false);
        }, 3000);
      } catch (error) {
        console.error('Error saving payment method:', error);
        
        // Show error feedback
        setFeedbackType('error');
        setFeedbackMessage('Failed to save payment method. Please try again.');
        setShowFeedback(true);
        
        // Clear feedback after animation completes
        setTimeout(() => {
          setShowFeedback(false);
        }, 3000);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 relative">
      {/* Feedback Notification */}
      <FeedbackNotification
        show={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={feedbackType}
        message={feedbackMessage}
        autoCloseTime={3000}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Payment Method Type
        </label>
        <Select
          value={formData.type}
          onValueChange={(value) => handleChange({ target: { name: 'type', value } })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select payment method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paypal">PayPal</SelectItem>
            <SelectItem value="bank">Bank Transfer</SelectItem>
            <SelectItem value="gcash">GCash</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {formData.type === 'paypal' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PayPal Email Address
          </label>
          <input
            type="email"
            name="details.email"
            value={formData.details.email || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder="your-email@example.com"
          />
          {errors['details.email'] && (
            <p className="mt-1 text-sm text-red-600">{errors['details.email']}</p>
          )}
        </div>
      )}

      {formData.type === 'bank' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Holder Name
            </label>
            <input
              type="text"
              name="details.accountName"
              value={formData.details.accountName || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Full name as it appears on your account"
            />
            {errors['details.accountName'] && (
              <p className="mt-1 text-sm text-red-600">{errors['details.accountName']}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name
            </label>
            <input
              type="text"
              name="details.bankName"
              value={formData.details.bankName || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="e.g., BDO, BPI, Metrobank"
            />
            {errors['details.bankName'] && (
              <p className="mt-1 text-sm text-red-600">{errors['details.bankName']}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number
            </label>
            <input
              type="text"
              name="details.accountNumber"
              value={formData.details.accountNumber || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Your bank account number"
            />
            {errors['details.accountNumber'] && (
              <p className="mt-1 text-sm text-red-600">{errors['details.accountNumber']}</p>
            )}
          </div>
        </>
      )}

      {formData.type === 'gcash' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            GCash Phone Number
          </label>
          <input
            type="text"
            name="details.phoneNumber"
            value={formData.details.phoneNumber || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder="09XXXXXXXXX"
          />
          {errors['details.phoneNumber'] && (
            <p className="mt-1 text-sm text-red-600">{errors['details.phoneNumber']}</p>
          )}
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          name="isDefault"
          id="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
        />
        <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
          Set as default payment method
        </label>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Save Payment Method
        </button>
      </div>
    </form>
  );
};

export default PaymentMethodForm;
